import { useState, useEffect } from 'react';

interface UseScrollOverlayParams {
  containerRef: React.RefObject<HTMLElement>;
  storageKey: string;
  hasOverflow: boolean;
}

interface UseScrollOverlayReturn {
  showOverlay: boolean;
  dismissOverlay: () => void;
}

export const useScrollOverlay = ({
  containerRef,
  storageKey,
  hasOverflow,
}: UseScrollOverlayParams): UseScrollOverlayReturn => {
  const [showOverlay, setShowOverlay] = useState(() => {
    if (!hasOverflow) return false;
    if (typeof window === 'undefined') return false;
    const hasScrolled = localStorage.getItem(storageKey);
    return hasScrolled !== 'true';
  });

  const dismissOverlay = () => {
    setShowOverlay(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, 'true');
    }
  };

  // React to hasOverflow changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (hasOverflow && !showOverlay) {
      const hasScrolled = localStorage.getItem(storageKey);
      if (hasScrolled !== 'true') {
        setShowOverlay(true);
      }
    } else if (!hasOverflow && showOverlay) {
      setShowOverlay(false);
    }
  }, [hasOverflow, storageKey, showOverlay]);

  useEffect(() => {
    const handleScroll = () => {
      if (showOverlay) {
        dismissOverlay();
      }
    };

    const container = containerRef.current;
    container?.addEventListener('scroll', handleScroll);

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [showOverlay]);

  return { showOverlay, dismissOverlay };
};
